import config from "../../utils/config";
import { Typography, Card, CardContent, Button, Grid, Box, Container, Chip } from "@mui/material";
import moment from "moment";
import parse from "html-react-parser";
import BuyEvent from "./BuyEvent";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

function EventDetails(props) {
  const { organizationId, eventId } = useParams();

  const [event, setEvent] = useState({});
  const [buying, setBuying] = useState(false);
  const [wishlisting, setWishlisting] = useState(false);
  const [specialPrice, setSpecialPrice] = useState(null);

  useEffect(() => {
    fetchEvent();
  }, []); //eslint-disable-line

  const fetchEvent = () => {
    let url = `${config.API_ENDPOINT}/events/${organizationId}/${eventId}`;

    if (props.shared) {
      url = `${config.API_ENDPOINT}/events/shared/${organizationId}/${eventId}`;
    }

    axios
      .get(url)
      .then((res) => {
        let event = res.data;

        if (props.shared) {
          let sharedEvent = event;
          event = sharedEvent.parentEvent;
          event.organization = sharedEvent.organization;
          event._id = sharedEvent._id;
          event.shared = false;
          event.seatsRemaining = sharedEvent.seatsRemaining;
          if (event.regularPrice != null) event.regularPrice += sharedEvent.extraPrice;
          if (event.esnCardPrice != null) event.esnCardPrice += sharedEvent.extraEsnCardPrice;
          if (event.esnCardBuyPrice != null)
            event.esnCardBuyPrice += sharedEvent.extraDiscountPrice;

          const parentOptions = (event.options || []).map(opt => {
            if (sharedEvent.requiredOptionOverride && sharedEvent.requiredOptionOverride.includes(opt._id)) {
              return { ...opt, required: true };
            }
            return opt;
          });

          event.options = [...parentOptions, ...(sharedEvent.options || [])];
        }

        setEvent(event);
      })
      .catch(() => {
        toast.error("An unexpected error has occurred");
      });
  };

  const showSpecialPrice = () => {
    if (event.esnCardBuyPrice != null && event.esnCardBuyPrice < event.regularPrice) {
      toast.info("Price Updated!");
      setSpecialPrice(event.esnCardBuyPrice);
    }
  };

  const hideSpecialPrice = () => {
    if (specialPrice != null) {
      toast.info("Price Updated!");
      setSpecialPrice(null);
    }
  };

  if ((!props.internal && !event.active) || event.shared) {
    return <div></div>;
  }

  let startDate = event.datetime
    ? moment(event.datetime).locale("en").format("dddd, DD/MM/YYYY HH:mm")
    : null;

  let endDate = event.datetimeEnd
    ? moment(event.datetimeEnd).locale("en").format("dddd, DD/MM/YYYY HH:mm")
    : null;

  let limitDate = event.datetimeLimit
    ? moment(event.datetimeLimit).locale("en").format("dddd, DD/MM/YYYY HH:mm")
    : null;

  const limitPassed = limitDate && event.datetimeLimit.valueOf() < moment().valueOf();

  let openDate = event.datetimeOpen
    ? moment(event.datetimeOpen).locale("en").format("dddd, DD/MM/YYYY HH:mm")
    : null;

  const dateNotOpened = openDate && moment().isBefore(moment(event.datetimeOpen));

  if (buying) {
    return (
      <BuyEvent
        event={event}
        wishlist={wishlisting}
        shared={props.shared}
        showSpecial={showSpecialPrice}
        hideSpecial={hideSpecialPrice}
      />
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f9fafb", minHeight: "100vh", pb: 4 }}>
      {/* Hero Image */}
      {event.imageURL && (
        <Box
          component="img"
          src={`${config.IMG_ENDPOINT}/uploads/${event.imageURL}`}
          alt={event.name}
          sx={{
            width: "100%",
            maxHeight: 400,
            objectFit: "contain",
            backgroundColor: "white",
          }}
        />
      )}

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Title */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: "#00aeef",
            textAlign: "center",
            mb: 4,
          }}
        >
          {event.name}
        </Typography>

        <Grid container spacing={3}>
          {/* Content Section */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        📍 Location:
                      </Typography>
                      <Typography variant="body2">{event.location}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        📅 Starts:
                      </Typography>
                      <Typography variant="body2">{startDate}</Typography>
                    </Box>
                    {endDate && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ⏰ Ends:
                        </Typography>
                        <Typography variant="body2">{endDate}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {event.esnCardRequired && (
                  <Chip
                    label="ESNcard Required"
                    icon={<span>🎫</span>}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  About this event
                </Typography>
                <Box
                  sx={{
                    "& p": { lineHeight: 1.8, mb: 1.5 },
                    "& strong": { fontWeight: 700 },
                    "& a": { color: "#00aeef", textDecoration: "none" },
                  }}
                >
                  {event.description ? parse(event.description) : <Typography sx={{color:"text.secondary"}}>No description available</Typography>}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar - Pricing & Actions */}
          <Grid item xs={12} md={4}>
            {/* Main Price Card */}
            <Card
              sx={{
                backgroundColor: specialPrice ? "#1a7ca8" : "#3b2f74",
                color: "white",
                mb: 2,
                transition: "all 0.3s ease",
              }}
            >
              <CardContent sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
                  {specialPrice ? "Special ESNcard Price" : "Price"}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, mb: 2, fontSize: "2.5rem", lineHeight: 1 }}
                >
                  {specialPrice != null ? specialPrice.toFixed(2) : event.regularPrice?.toFixed(2)}{" "}
                  <span style={{ fontSize: "1.5rem" }}>€</span>
                </Typography>
              </CardContent>
            </Card>

            {/* Alternative Pricing */}
            {!event.esnCardRequired && event.esnCardPrice != null && specialPrice == null && (
              <Card sx={{ mb: 2, backgroundColor: "#00aeef", color: "white" }}>
                <CardContent sx={{ p: 2.5, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    ESNcard Holder Price
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {event.esnCardPrice.toFixed(2)} €
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Info Cards */}

            {dateNotOpened && (
              <Card sx={{ mb: 2, borderLeft: "4px solid #ffc107" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#856404" }}>
                    🔒 Purchases Open
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {openDate}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {limitDate && (
              <Card sx={{ mb: 2, borderLeft: "4px solid #ff6b6b" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#ff6b6b" }}>
                    ⏳ Registration Deadline
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {limitDate}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {event.seats != null && (
              <Card sx={{ mb: 2, borderLeft: "4px solid #00aeef" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#00aeef" }}>
                    🎫 Seats Remaining
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {event.seatsRemaining > 0 ? `${event.seatsRemaining} available` : "No seats available"}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Action Button */}
            {event.name &&
              (event.seatsRemaining == null || event.seatsRemaining > 0) &&
              !limitPassed &&
              !dateNotOpened && (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.5,
                    backgroundColor: "#00aeef",
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    mb: 1.5,
                    "&:hover": {
                      backgroundColor: "#0095d1",
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() => setBuying(true)}
                >
                  Purchase Ticket
                </Button>
              )}

            {event.name && event.wishlistEnabled && (event.seatsRemaining === 0 || limitPassed) && (
              <Button
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  backgroundColor: "#3b2f74",
                  color: "white",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#2e2561",
                  },
                }}
                onClick={() => {
                  setBuying(true);
                  setWishlisting(true);
                }}
              >
                Add to Wishlist
              </Button>
            )}

            {event.seatsRemaining === 0 && !event.wishlistEnabled && (
              <Card sx={{ backgroundColor: "#fff3cd", borderLeft: "4px solid #ffc107" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: "#856404", fontWeight: 600 }}>
                    ⚠️ Event is fully booked
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default EventDetails;
